import { message, notification } from 'antd';
import _ from 'lodash';
import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import { toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';

const api = bindServices('manageProcess');
import { queryEditActive, updateFavorite } from './service';
import { filtersConfig } from './utils';

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const initialState = {
  data: [],
  newProcess: false,
  selectedItem: null,
  formConfig: null,
  listUserGroupUser: [],
  pagination: meta,
  view: false,
  filtersConfig,
  orderBy: '',
  id: null,
  favorite: false,
  searchCriteria: '',
  quickAccessOptions: null,
};

const Model = {
  namespace: 'manageProcess',
  state: initialState,
  effects: {
    *fetchList({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'process/allowed',
          ...payload,
          ...CoreQueries.ManageProcess.paged,
        });

        yield put({
          type: 'queryProcess',
          payload: {
            ...res,
            ..._.pick(payload, ['limit', 'page']),
          },
        });
      } catch (e) {}
    },

    *fetchQuickAccessOptions({ payload }, { call, put }) {
      try {
        const res = payload
          ? yield call(api.getById, { route: 'process/quickAccess', ...payload })
          : null;
        yield put({ type: 'queryQuickAccessOptions', payload: res });
      } catch (e) {}
    },

    *bulkUpload({ payload }, { call, put }) {
      try {
        const { fileUploadCallback, ...data } = payload.data;
        const res = yield call(api.update, {
          method: 'post',
          route: `bulkRequestTask/load/${payload.id}`,
          data,
        });

        fileUploadCallback?.();

        if (res.message) {
          const [message, description] = _.split(res.message, '.');
          notification.destroy();
          notification.success({
            ...(message && { message }),
            ...(description && { description }),
            style: { minWidth: 600 },
            duration: 10,
          });
        }

        yield put({ type: 'fetchList', payload: payload.params });
      } catch (e) {}
    },

    *bulkDownload({ payload }, { call }) {
      try {
        message.loading(getIntl().formatMessage({ id: 'process.text.bulk.download.loading' }), 0);
        const res = yield call(api.getById, {
          route: 'process/downloadBulkRequestsTemplate',
          ...payload,
        });

        message.destroy();
        message.success(getIntl().formatMessage({ id: 'process.text.bulk.download.success' }), 5);

        const link = document.createElement('a');
        link.href = `data:${res.contentType};base64,${res.content}`;
        link.download = res.fileName;

        link.click();
        link.remove();
      } catch (e) {
        message.destroy();
      }
    },

    *addNewProcess({ payload }, { put }) {
      yield put({ type: 'addProcess', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrViewProcess', payload });
      }
    },

    *closeNewProcess({ payload }, { put }) {
      yield put({ type: 'closeProcess', payload });
    },

    *editItemProcess({ payload }, { call, put }) {
      try {
        const table = yield call(api.getById, {
          route: 'process',
          id: payload.id,
          params: payload.params,
        });
        yield put({
          type: 'editProcess',
          payload: { ...payload, item: table },
        });
      } catch (e) {
        yield put({ type: 'global/loadingItem', payload: false });
      }
    },

    *saveNewProcess({ payload, params }, { call, put }) {
      try {
        const res = yield call(api.create, {
          route: 'process',
          data: payload,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'saveProcess', payload: res });
        // Reload the list in the processes model (used by TableGridLayout)
        yield put({ type: 'processes/fetchList', payload: { ...params, readPriority: true } });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *saveEditProcess({ payload, params }, { call, put }) {
      try {
        const res = yield call(api.update, {
          route: 'process',
          data: payload,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'saveProcess', payload: res });
        yield put({ type: 'processes/fetchList', payload: { ...params, readPriority: true } });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *deleteItem({ id, params, view }, { call, put }) {
      try {
        yield put({ type: `global/modalConfirm`, state: false, id: null });
        const res = yield call(api.apiRoute, {
          route: `process/${id}`,
          method: 'delete',
        });
        yield put({ type: 'saveProcess', payload: res });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'processes/fetchList', payload: { ...params, readPriority: true } });
      } catch (error) {
        yield put({ type: `global/modalConfirm`, state: false, id: null });
        if (error.message) {
          yield put({
            type: `global/showNotifications${view ? 'Form' : 'Global'}`,
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: view,
            },
          });
        }
      }
    },

    *v1ProcessConfig({ payload }, { call, put }) {
      try {
        const res = yield call(api.apiRoute, { route: `migration/v1ConfigUrl/${payload.id}` });
        yield put({
          type: 'queryV1ProcessConfig',
          payload: { ...payload, ...res },
        });
      } catch (e) {}
    },

    *editActive({ payload, params }, { call, put }) {
      try {
        yield call(queryEditActive, params);
        yield put({ type: 'editActives', payload });
      } catch (e) {}
    },

    *setFavorite({ payload }, { put }) {
      yield put({ type: 'queryFavorite' });
      yield put({ type: 'processes/setFavorite', payload });
    },

    *showItemProcesss({ view }, { put }) {
      yield put({ type: 'showEditItemProcess', view });
    },

    *hideAllForms({ payload = 'hideForms' }, { put }) {
      yield put({ type: payload });
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *eventSorterUserGroup({ payload }, { put }) {
      yield put({ type: 'sorterUserGroup', payload });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'cleanUp' });
    },
  },

  reducers: {
    queryProcess(state, { payload }) {
      return {
        ...state,
        favorite: false,
        data: payload.items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryQuickAccessOptions(state, { payload }) {
      return {
        ...state,
        quickAccessOptions: payload,
      };
    },

    addProcess(state, { payload }) {
      return {
        ...state,
        newProcess: payload.state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
      };
    },

    editOrViewProcess(state) {
      return {
        ...state,
        selectedItem: {},
      };
    },

    editProcess(state, { payload }) {
      return {
        ...state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
      };
    },

    closeProcess(state, { payload }) {
      return {
        ...state,
        newProcess: payload.state,
        selectedItem: state.selectedItem,
        view: state.view,
        id: state.id,
      };
    },

    saveProcess(state, { payload }) {
      return {
        ...state,
        saveUser: payload,
        newProcess: false,
        selectedItem: null,
        view: false,
      };
    },

    editActives(state, { payload }) {
      const list = state.list.map((item) => (item.id === payload.id ? payload : item));

      return {
        ...state,
        list,
      };
    },

    showEditItemProcess(state, { view }) {
      return {
        ...state,
        view,
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
      };
    },

    queryFavorite(state) {
      return {
        ...state,
        favorite: true,
      };
    },

    queryV1ProcessConfig(state, { payload }) {
      return {
        ...state,
        v1ProcessConfig: {
          processId: payload?.id,
          v1ConfigURL: payload?.url,
        },
      };
    },

    hideForms(state) {
      return {
        ...state,
        newProcess: false,
        newUserOnGroup: false,
        processSearchCriteria: null,
        selectedItem: null,
        id: null,
        orderBy: '',
        view: false,
        searchCriteria: '',
      };
    },

    cleanUp() {
      return initialState;
    },
  },
};

export default Model;
